import { Vector2 } from "../../../common/vector2.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { ImagesHolder } from "../../../image_loader/images_holder.js";
import { Easing } from "../../../transforms/transform_base.js";
import { TransformScale } from "../../../transforms/transform_scale.js";
import { TransformStraightMove } from "../../../transforms/transform_straight_move.js";
import { BlurredShadowBlob } from "../../common_ui_elements/blurred_shadow_blob.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { DiagonalStripesOverlay } from "../../common_ui_elements/diagonal_stripes_overlay.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { UIElement } from "../../common_ui_elements/ui_element.js";
import { UIElementHolder } from "../../common_ui_elements/ui_element_holder.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { UIProgressBar } from "../../common_ui_elements/ui_progress_bar.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../../common_ui_elements/ui_text_element.js";

export class LoadingScreen extends UIElement
{
  private static readonly SUBSTRATE_GROW_MS = 200;
  private static readonly SUBSTRATE_SETTLE_MS = 90;
  private static readonly ROYAL_MS = 200;
  private static readonly SMASH_FADE_MS = 150;
  private static readonly SMASH_START_SCALE = 3;
  private static readonly SMASH_GROW_MS = 280;

  private static readonly DIP_TARGET_SCALE = 0.8;
  private static readonly DIP_MS = 130;
  private static readonly RECOVER_MS = 200;

  private static readonly CROWN_FALL_MS = 180;
  private static readonly CROWN_BOUNCE_UP_MS = 55;
  private static readonly CROWN_BOUNCE_DOWN_MS = 70;

  private static readonly MAIN_SEQUENCE_MS =
    LoadingScreen.SUBSTRATE_GROW_MS + LoadingScreen.ROYAL_MS + LoadingScreen.SMASH_GROW_MS
    + LoadingScreen.DIP_MS + LoadingScreen.RECOVER_MS;

  public static readonly ENTRANCE_DURATION_MS =
    LoadingScreen.MAIN_SEQUENCE_MS
    + LoadingScreen.CROWN_FALL_MS + LoadingScreen.CROWN_BOUNCE_UP_MS + LoadingScreen.CROWN_BOUNCE_DOWN_MS;

  private progress_bar!: UIProgressBar;
  private stripes!: DiagonalStripesOverlay;
  private percentage_text!: UITextElement;

  private bar_x = 0;
  private bar_y = 0;
  private bar_w = 0;
  private bar_h = 0;
  private fill_border = 0;
  private fill_corner_size = 0;

  private target_progress = 0;
  private last_shown_percentage = -1;

  constructor( ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number )
  {
    super( ctx, x, y, width, height, false, false );

    this.createBackground();
    this.createLogo();
    this.createProgressBar();
    this.createPercentageText();
    this.updateFillGeometry();
  }

  private createBackground(): void
  {
    const screen_w = this.rect.getW();
    const screen_h = this.rect.getH();

    const image = ImagesHolder.getImage( ImgID.UI_LoadingBg );
    const scale = Math.max( screen_w / image.width, screen_h / image.height );

    const bg_w = image.width * scale;
    const bg_h = image.height * scale;
    const bg_x = ( screen_w - bg_w ) / 2;
    const bg_y = ( screen_h - bg_h ) / 2;

    const bg = new UIImage(
      this.ctx, bg_x, bg_y, bg_w, bg_h, ImgID.UI_LoadingBg
    );
    bg.setClickable( false );
    this.addChild( bg );
  }

  private static containImageInRect( img_id: ImgID, x: number, y: number, w: number, h: number )
  {
    const image = ImagesHolder.getImage( img_id );
    const native_ratio = image.width / image.height;
    const box_ratio = w / h;

    let fit_w = w;
    let fit_h = h;

    if ( native_ratio > box_ratio )
      fit_h = w / native_ratio;
    else
      fit_w = h * native_ratio;

    const cx = x + w / 2;
    const cy = y + h / 2;

    return { x: cx - fit_w / 2, y: cy - fit_h / 2, w: fit_w, h: fit_h };
  }

  private static readonly SUBSTRATE_W = 706;
  private static readonly SUBSTRATE_H = 438;
  private static readonly SUBSTRATE_Y = 173;
  private static readonly GROUP_Y = 47;

  private createLogo(): void
  {
    const content_x = this.rect.getW() / 2 - LayoutScaler.widthFromDesign( 1080 ) / 2;
    const now = performance.now();

    const group_h = LoadingScreen.SUBSTRATE_Y + LoadingScreen.SUBSTRATE_H - LoadingScreen.GROUP_Y;

    const group = new UIElementHolder(
      this.ctx,
      content_x + LayoutScaler.xFromDesign( 0 ),
      LayoutScaler.yFromDesign( LoadingScreen.GROUP_Y ),
      LayoutScaler.widthFromDesign( 1080 ),
      LayoutScaler.heightFromDesign( group_h )
    );
    group.setClickable( false );
    this.addChild( group );

    const crowl = this.createCrowl( group );
    const substrate = this.createLogoSubstrate( group );
    const royal = this.createRoyal( group );
    const smash = this.createSmash( this, content_x );

    this.animateLogo( group, crowl, substrate, royal, smash, now );
  }

  private animateLogo( group: UIElement, crowl: UIImage, substrate: UIImage, royal: UIImage, smash: UIImage, now: number ): void
  {
    const SUBSTRATE_GROW_MS = LoadingScreen.SUBSTRATE_GROW_MS;
    const SUBSTRATE_SETTLE_MS = LoadingScreen.SUBSTRATE_SETTLE_MS;
    const ROYAL_MS = LoadingScreen.ROYAL_MS;
    const SMASH_FADE_MS = LoadingScreen.SMASH_FADE_MS;
    const SMASH_START_SCALE = LoadingScreen.SMASH_START_SCALE;
    const SMASH_GROW_MS = LoadingScreen.SMASH_GROW_MS;
    const DIP_TARGET_SCALE = LoadingScreen.DIP_TARGET_SCALE;
    const DIP_MS = LoadingScreen.DIP_MS;
    const RECOVER_MS = LoadingScreen.RECOVER_MS;

    const t_substrate_peak = now + SUBSTRATE_GROW_MS;
    const t_royal_end = t_substrate_peak + ROYAL_MS;
    const t_smash_1x = t_royal_end + SMASH_GROW_MS;
    const t_dip_end = t_smash_1x + DIP_MS;

    substrate.setScale( new Vector2( 0.1, 0.1 ) );
    substrate.addTransform( new TransformScale(
      substrate, true, new Vector2( 0.1, 0.1 ), new Vector2( 1.2, 1.2 ),
      now, SUBSTRATE_GROW_MS, { easing: Easing.EaseOutQuad }
    ) );
    substrate.addTransform( new TransformScale(
      substrate, true, new Vector2( 1.2, 1.2 ), new Vector2( 1, 1 ),
      t_substrate_peak, SUBSTRATE_SETTLE_MS, { easing: Easing.EaseInOutQuad }
    ) );

    royal.setScale( new Vector2( 0, 0 ) );
    royal.addTransform( new TransformScale(
      royal, true, new Vector2( 0, 0 ), new Vector2( 1, 1 ),
      t_substrate_peak, ROYAL_MS, { easing: Easing.EaseOutQuad }
    ) );

    smash.setScale( new Vector2( SMASH_START_SCALE, SMASH_START_SCALE ) );
    smash.setOpacity( 0 );
    smash.fadeIn( SMASH_FADE_MS, t_royal_end, Easing.EaseOutQuad );
    smash.addTransform( new TransformScale(
      smash, true, new Vector2( SMASH_START_SCALE, SMASH_START_SCALE ), new Vector2( 1, 1 ),
      t_royal_end, SMASH_GROW_MS, { easing: Easing.Linear }
    ) );
    smash.addTransform( new TransformScale(
      smash, true, new Vector2( 1, 1 ), new Vector2( DIP_TARGET_SCALE, DIP_TARGET_SCALE ),
      t_smash_1x, DIP_MS, { easing: Easing.Linear }
    ) );
    smash.addTransform( new TransformScale(
      smash, true, new Vector2( DIP_TARGET_SCALE, DIP_TARGET_SCALE ), new Vector2( 1, 1 ),
      t_dip_end, RECOVER_MS, { easing: Easing.EaseOutQuad }
    ) );

    group.addTransform( new TransformScale(
      group, true, new Vector2( 1, 1 ), new Vector2( DIP_TARGET_SCALE, DIP_TARGET_SCALE ),
      t_smash_1x, DIP_MS, { easing: Easing.Linear }
    ) );
    group.addTransform( new TransformScale(
      group, true, new Vector2( DIP_TARGET_SCALE, DIP_TARGET_SCALE ), new Vector2( 1, 1 ),
      t_dip_end, RECOVER_MS, { easing: Easing.EaseOutQuad }
    ) );

    const crowl_pos = crowl.pixelPosImportant();
    const crowl_x = crowl_pos.x;
    const crowl_final_y = crowl_pos.y;
    const crowl_drop_y = crowl_final_y - LayoutScaler.heightFromDesign( 350 );
    const crowl_bounce_y = crowl_final_y - LayoutScaler.heightFromDesign( 18 );

    crowl.setPixelPos( new Vector2( crowl_x, crowl_drop_y ), true );

    const t_crown_fall_start = now + LoadingScreen.MAIN_SEQUENCE_MS;
    const t_crown_land = t_crown_fall_start + LoadingScreen.CROWN_FALL_MS;
    const t_crown_bounce_peak = t_crown_land + LoadingScreen.CROWN_BOUNCE_UP_MS;

    crowl.addTransform( new TransformStraightMove(
      crowl, true, null, new Vector2( crowl_x, crowl_final_y ),
      t_crown_fall_start, LoadingScreen.CROWN_FALL_MS, { easing: Easing.EaseInQuad }
    ) );
    crowl.addTransform( new TransformStraightMove(
      crowl, true, null, new Vector2( crowl_x, crowl_bounce_y ),
      t_crown_land, LoadingScreen.CROWN_BOUNCE_UP_MS, { easing: Easing.EaseOutQuad }
    ) );
    crowl.addTransform( new TransformStraightMove(
      crowl, true, null, new Vector2( crowl_x, crowl_final_y ),
      t_crown_bounce_peak, LoadingScreen.CROWN_BOUNCE_DOWN_MS, { easing: Easing.EaseInQuad }
    ) );
  }

  private createCrowl( parent: UIElement ): UIImage
  {
    const crowl_w = LayoutScaler.widthFromDesign( 218 );
    const crowl_h = LayoutScaler.heightFromDesign( 214 );
    const crowl_x = LayoutScaler.xFromDesign( ( 1080 - 218 ) / 2 );
    const crowl_y = LayoutScaler.yFromDesign( 0 );

    const crowl = new UIImage(
      this.ctx, crowl_x, crowl_y, crowl_w, crowl_h,
      ImgID.UI_LoadingLogoCrowl
    );
    crowl.setClickable( false );
    parent.addChild( crowl );
    return crowl;
  }

  private createLogoSubstrate( parent: UIElement ): UIImage
  {
    const x = ( 1080 - LoadingScreen.SUBSTRATE_W ) / 2;
    const rel_y = LoadingScreen.SUBSTRATE_Y - LoadingScreen.GROUP_Y;

    const substrate = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( x ),
      LayoutScaler.yFromDesign( rel_y ),
      LayoutScaler.widthFromDesign( LoadingScreen.SUBSTRATE_W ),
      LayoutScaler.heightFromDesign( LoadingScreen.SUBSTRATE_H ),
      ImgID.UI_LoadingLogoSubstrate
    );
    substrate.setClickable( false );
    parent.addChild( substrate );
    return substrate;
  }

  private createRoyal( parent: UIElement ): UIImage
  {
    const box = { x: ( 1080 - 493 ) / 2, y: 205 - LoadingScreen.GROUP_Y, w: 493, h: 201 };
    const rect = LoadingScreen.containImageInRect( ImgID.UI_LoadingLogoRoyal, box.x, box.y, box.w, box.h );

    const royal = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( rect.x ),
      LayoutScaler.yFromDesign( rect.y ),
      LayoutScaler.widthFromDesign( rect.w ),
      LayoutScaler.heightFromDesign( rect.h ),
      ImgID.UI_LoadingLogoRoyal
    );
    royal.setClickable( false );
    parent.addChild( royal );
    return royal;
  }

  private createSmash( parent: UIElement, content_x: number ): UIImage
  {
    const box = { x: ( 1080 - 575 ) / 2, y: 387, w: 575, h: 161 };
    const rect = LoadingScreen.containImageInRect( ImgID.UI_LoadingLogoSmash, box.x, box.y, box.w, box.h );

    const smash = new UIImage(
      this.ctx,
      content_x + LayoutScaler.xFromDesign( rect.x ),
      LayoutScaler.yFromDesign( rect.y ),
      LayoutScaler.widthFromDesign( rect.w ),
      LayoutScaler.heightFromDesign( rect.h ),
      ImgID.UI_LoadingLogoSmash
    );
    smash.setClickable( false );
    parent.addChild( smash );
    return smash;
  }

  private createProgressBar(): void
  {
    const content_x = this.rect.getW() / 2 - LayoutScaler.widthFromDesign( 1080 ) / 2;

    this.bar_x = content_x + LayoutScaler.xFromDesign( 286 );
    this.bar_y = LayoutScaler.yFromLayouts( 1608, 2019 );
    this.bar_w = LayoutScaler.widthFromDesign( 508 );
    this.bar_h = LayoutScaler.heightFromDesign( 58 );

    this.fill_border = LayoutScaler.widthFromDesign( 12 );
    this.fill_corner_size = LayoutScaler.widthFromDesign( 19 );

    this.progress_bar = new UIProgressBar(
      this.ctx, this.bar_x, this.bar_y, this.bar_w, this.bar_h,
      {
        base_img_id: ImgID.UI_LoadingProgressBarBase,
        fill_img_id: ImgID.UI_LoadingProgressBarFill,
        base_nine_slice: "loading_bar_base",
        fill_nine_slice: "loading_bar_fill",
        border_width: this.fill_border,
        base_corner_size: LayoutScaler.widthFromDesign( 28 ),
        fill_corner_size: this.fill_corner_size,
        initial_progress: 0,
      }
    );
    this.progress_bar.setClickable( false );
    this.addChild( this.progress_bar );

    this.stripes = new DiagonalStripesOverlay(
      this.ctx, 0, 0, 0, 0,
      {
        color: "#FFFFFF",
        opacity: 0.35,
        stripe_width: LayoutScaler.widthFromDesign( 24 ),
        gap_width: LayoutScaler.widthFromDesign( 24 ),
        angle_deg: 22,
        speed: LayoutScaler.widthFromDesign( 140 ),
        corner_radius: this.fill_corner_size,
      }
    );
    this.stripes.setClickable( false );
    this.addChild( this.stripes );
  }

  private createPercentageText(): void
  {
    const text_w = LayoutScaler.widthFromDesign( 220 );
    const text_h = LayoutScaler.heightFromDesign( 70 );
    const center_x = this.bar_x + this.bar_w / 2;
    const center_y = this.bar_y + this.bar_h + LayoutScaler.heightFromDesign( 46 );

    const blob = new BlurredShadowBlob(
      this.ctx,
      center_x - LayoutScaler.widthFromDesign( 75 ),
      center_y - LayoutScaler.heightFromDesign( 30 ),
      LayoutScaler.widthFromDesign( 150 ),
      LayoutScaler.heightFromDesign( 60 ),
      {
        color: "#000000",
        opacity: 0.5,
        blur: LayoutScaler.widthFromDesign( 18 ),
      }
    );
    blob.setClickable( false );
    this.addChild( blob );

    this.percentage_text = new UITextElement(
      this.ctx,
      center_x - text_w / 2,
      center_y - text_h / 2,
      text_w,
      text_h,
      {
        text: "0%",
        font_size: LayoutScaler.textSizeFromDesign( 44 ),
        font_weight: 800,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: false,
      }
    );
    this.percentage_text.setClickable( false );
    this.addChild( this.percentage_text );
  }

  private updateFillGeometry(): void
  {
    const progress = this.progress_bar.getProgress();

    const fill_max_w = Math.max( 0, this.bar_w - this.fill_border * 2 );
    const fill_h = Math.max( 0, this.bar_h - this.fill_border * 2 );

    const raw_w = fill_max_w * progress;
    const min_w = this.fill_corner_size * 2;
    const fill_w = progress > 0 && raw_w > 0
      ? Math.min( Math.max( raw_w, min_w ), fill_max_w )
      : 0;

    this.stripes.setPixelPos( new Vector2( this.bar_x + this.fill_border, this.bar_y + this.fill_border ), true );
    this.stripes.setSize( fill_w, fill_h );

    const shown_percentage = Math.round( progress * 100 );
    if ( shown_percentage !== this.last_shown_percentage )
    {
      this.last_shown_percentage = shown_percentage;
      this.percentage_text.setText( `${shown_percentage}%` );
    }
  }

  /** Sets the bar as is; StartupProgress already smooths the value per frame. */
  public setShownProgress( t: number ): void
  {
    const clamped = Math.max( this.target_progress, Math.min( 1, t ) );
    if ( clamped - this.target_progress < 0.0005 )
      return;
    this.target_progress = clamped;
    this.progress_bar.setProgress( clamped );
    this.setShouldBeRendered();
  }

  public update( now: number ): void
  {
    this.updateBasic( now );
    this.updateFillGeometry();
  }

  public name(): string
  {
    return "LoadingScreen";
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() )
      return;

    this.startRender();
    this.finishRender();
  }
}
